import {useMemo} from 'react';
import {useIntl} from 'react-intl';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {parseDepositPercentage} from '@amblique/king-living/app/pages/checkout/util/payment-amount-utils';

/**
 * Fallback used when `depositBalanceDueDays` is missing or invalid (mirrors the
 * SFCC metadata `default-value`). Keeps the deposit copy ("balance due N days
 * before delivery") sensible even without site data (e.g. local dev).
 */
export const DEFAULT_DEPOSIT_BALANCE_DUE_DAYS = 7;

/**
 * Coerce a raw `depositBalanceDueDays` value into a positive integer day count.
 *
 * @param {*} raw
 * @returns {number} positive integer, or {@link DEFAULT_DEPOSIT_BALANCE_DUE_DAYS}.
 */
const parseBalanceDueDays = (raw) => {
  const parsed = typeof raw === 'string' ? parseInt(raw, 10) : raw;

  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : DEFAULT_DEPOSIT_BALANCE_DUE_DAYS;
};

/**
 * Format a deposit percentage for display (e.g. `25` → `"25%"`).
 *
 * @param {number} percentage
 * @param {import('react-intl').IntlShape} intl
 * @returns {string}
 */
const formatDepositPercentageLabel = (percentage, intl) => intl.formatNumber(percentage / 100, {maximumFractionDigits: 2, style: 'percent'});

/**
 * Deposit configuration for checkout — the single source of truth for both the
 * deposit **rate/percentage** and the balance **due window** (days before
 * delivery). Both come from the Checkout site-preference group
 * (`depositPercentage`, `depositBalanceDueDays`).
 *
 * Values are read from the MRT `{SITE}_CLIENT_DATA` baked into `site.preferences`.
 *
 * @returns {{
 *   rawDepositPercentage: *,
 *   depositPercentage: number|null,
 *   depositRate: number|null,
 *   isDepositPercentageValid: boolean,
 *   formattedDepositPercentage: string|null,
 *   balanceDueDays: number
 * }}
 */
export const useDepositPercentage = () => {
  const intl = useIntl();
  const rawDepositPercentage = useSitePreference('depositPercentage');
  const rawBalanceDueDays = useSitePreference('depositBalanceDueDays');

  return useMemo(() => {
    const {depositPercentage, depositRate, isDepositPercentageValid} =
      parseDepositPercentage(rawDepositPercentage);

    const formattedDepositPercentage = isDepositPercentageValid ?
      formatDepositPercentageLabel(depositPercentage, intl) :
      null;

    return {
      rawDepositPercentage,
      depositPercentage,
      depositRate,
      isDepositPercentageValid,
      formattedDepositPercentage,
      balanceDueDays: parseBalanceDueDays(rawBalanceDueDays),
    };
  }, [rawDepositPercentage, rawBalanceDueDays, intl]);
};
